/**
 * Defensive adapter that coerces the raw `unknown` payload returned by
 * `runTtfQuery` into the typed `TtfApiResponse` shape that the Excel
 * host's `factset.Spreadsheet.InsertTtfApiResponse` intent expects.
 *
 * Why a separate adapter?
 *  - `runTtfQuery` types the upstream payload as `unknown` because the
 *    FactSet TTF response shape is owned by FactSet and may evolve.
 *  - The Excel cell-write path needs a strongly-typed `TtfApiResponse`
 *    to feed the host wrapper.
 *  - Centralising the shape check here keeps the cell-write path free
 *    of inline `as`/`is` ladder noise, and gives us a single place to
 *    log when the upstream contract drifts.
 *
 * Contract:
 *  - Returns a `TtfApiResponse` when the payload has the minimum shape
 *    we require (`results: [{ formula: string }, ...]`).
 *  - Returns `null` when ANY required field is missing or wrongly
 *    typed. Callers treat `null` as a TTF failure and fall back to the
 *    supplied formula.
 *  - Never throws — any unexpected shape returns `null` plus a
 *    `console.warn` so the operator can spot upstream contract drift.
 */

import type {
  TtfApiResponse,
  TtfApiResponseFormulaConversation,
  TtfApiResponseFormulaInputs,
  TtfApiResponseFqlConversion,
  TtfApiResponseFormulaDetails,
  TtfApiResponseResult,
  TtfApiResponseValidation,
} from "./spreadsheetIntents";

/** Narrow `value` to a plain object. Excludes arrays and `null`. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The FactSet TTF API wraps each item in `results` in an extra
 * `{ result: { formula, ... } }` envelope. We accept either the
 * wrapped (`{ result: { formula, ... } }`) or the flat
 * (`{ formula, ... }`) shape so that the adapter is resilient to
 * either contract — production responses today carry the wrapper,
 * older fixtures / hand-rolled tests historically did not, and the
 * upstream could drop the wrapper in a future revision.
 *
 * Detection rule: if `raw` is an object whose top-level `formula`
 * field is missing/non-string but it has a nested `result.formula`
 * that IS a non-empty string, treat the inner object as the actual
 * result payload.
 */
function unwrapResultItem(raw: unknown): unknown {
  if (!isRecord(raw)) return raw;
  if (typeof raw.formula === "string" && raw.formula.length > 0) {
    return raw;
  }
  if (isRecord(raw.result) && typeof raw.result.formula === "string" && raw.result.formula.length > 0) {
    return raw.result;
  }
  return raw;
}

function adaptValidation(raw: unknown): TtfApiResponseValidation | undefined {
  if (!isRecord(raw)) return undefined;
  if (typeof raw.status !== "boolean") return undefined;
  return raw as unknown as TtfApiResponseValidation;
}

function adaptFqlConversion(raw: unknown): TtfApiResponseFqlConversion | undefined {
  if (!isRecord(raw)) return undefined;
  if (typeof raw.fql_with_inputs !== "string") return undefined;
  const inputsRaw = raw.inputs;
  let inputs: TtfApiResponseFormulaInputs | undefined;
  if (inputsRaw === undefined) {
    inputs = undefined;
  } else if (isRecord(inputsRaw)) {
    const coerced: TtfApiResponseFormulaInputs = {};
    let valid = true;
    for (const [key, entry] of Object.entries(inputsRaw)) {
      if (!isRecord(entry) || typeof entry.cell !== "string" || typeof entry.value !== "string") {
        valid = false;
        break;
      }
      coerced[key] = { cell: entry.cell, value: entry.value };
    }
    if (!valid) return undefined;
    inputs = coerced;
  } else {
    return undefined;
  }
  return inputs ? { fql_with_inputs: raw.fql_with_inputs, inputs } : { fql_with_inputs: raw.fql_with_inputs };
}

function adaptFormulaDetails(raw: unknown): TtfApiResponseFormulaDetails | undefined {
  if (!isRecord(raw)) return undefined;
  return raw as unknown as TtfApiResponseFormulaDetails;
}

function adaptFormulaConversation(raw: unknown): TtfApiResponseFormulaConversation | undefined {
  if (!isRecord(raw)) return undefined;
  if (typeof raw.commentary !== "string" && typeof raw.id !== "string") return undefined;
  return raw as unknown as TtfApiResponseFormulaConversation;
}

function adaptTicker(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  if (!raw.every((entry) => typeof entry === "string")) return undefined;
  return raw as string[];
}

function adaptResult(raw: unknown): TtfApiResponseResult | null {
  if (!isRecord(raw)) return null;
  if (typeof raw.formula !== "string" || raw.formula.length === 0) return null;
  const ticker = adaptTicker(raw.ticker);
  const validation = adaptValidation(raw.validation);
  const fqlConversion = adaptFqlConversion(raw.fql_conversion);
  const formulaDetails = adaptFormulaDetails(raw.formula_details);
  const formulaConversation = adaptFormulaConversation(raw.formula_conversation);
  const result: TtfApiResponseResult = { formula: raw.formula };
  if (ticker !== undefined) result.ticker = ticker;
  if (typeof raw.caccess_check === "string") {
    result.caccess_check = raw.caccess_check;
  }
  if (validation !== undefined) result.validation = validation;
  if (fqlConversion !== undefined) result.fql_conversion = fqlConversion;
  if (formulaDetails !== undefined) result.formula_details = formulaDetails;
  if (formulaConversation !== undefined) result.formula_conversation = formulaConversation;
  return result;
}

/**
 * Coerce a raw TTF API payload into a typed `TtfApiResponse` for the
 * host intent. Returns `null` when the shape is unusable.
 */
export function adaptTtfApiResponse(raw: unknown): TtfApiResponse | null {
  if (!isRecord(raw)) {
    console.warn("[FACTSET_TTF] adapt_failed: top-level payload is not an object");
    return null;
  }
  const resultsRaw = raw.results;
  if (!Array.isArray(resultsRaw) || resultsRaw.length === 0) {
    console.warn(
      "[FACTSET_TTF] adapt_failed: missing or empty 'results' array on TTF payload"
    );
    return null;
  }
  const results: TtfApiResponseResult[] = [];
  for (let i = 0; i < resultsRaw.length; i += 1) {
    const adapted = adaptResult(unwrapResultItem(resultsRaw[i]));
    if (!adapted) {
      console.warn(
        `[FACTSET_TTF] adapt_failed: results[${i}] missing a non-empty string 'formula'`
      );
      return null;
    }
    results.push(adapted);
  }
  return { results };
}
